import React, { useState, useEffect, useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { TrophyIcon } from '@heroicons/react/24/outline';
import SidebarLinkGroup from "./SidebarLinkGroup";
import ClientSelector from './components/ClientSelector';
import LiveCampaignStatus from './components/LiveCampaignStatus';

import { useUser } from './userContext';

function Sidebar({
  sidebarOpen,
  setSidebarOpen,
  variant = 'default',
}) {
  const location = useLocation();
  const { pathname } = location;
  const { user, userProfile, subscriptionType, businessProfile } = useUser();
  const userName = userProfile?.firstName;

  // Admin permission logic (same as AdminRoute.jsx)
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const isDevSite = window.location.hostname === 'smilesflowdev-1514f.web.app';
  const isProduction = window.location.hostname === 'groupchatheroes.com';
  const isAdmin = isLocalhost || isDevSite || 
    (isProduction && (userProfile?.email === 'rob@groupchatheroes.com' || user?.email === 'rob@groupchatheroes.com'));

  const trigger = useRef(null);
  const sidebar = useRef(null);

  const storedSidebarExpanded = localStorage.getItem("sidebar-expanded");
  const [sidebarExpanded, setSidebarExpanded] = useState(storedSidebarExpanded === null ? false : storedSidebarExpanded === "true");

  // close on click outside
  useEffect(() => {
    const clickHandler = ({ target }) => {
      if (!sidebar.current || !trigger.current) return;
      if (!sidebarOpen || sidebar.current.contains(target) || trigger.current.contains(target)) return;
      setSidebarOpen(false);
    };
    document.addEventListener("click", clickHandler);
    return () => document.removeEventListener("click", clickHandler);
  });

  // close if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }) => {
      if (!sidebarOpen || keyCode !== 27) return;
      setSidebarOpen(false);
    };
    document.addEventListener("keydown", keyHandler);
    return () => document.removeEventListener("keydown", keyHandler);
  });

  useEffect(() => {
    localStorage.setItem("sidebar-expanded", sidebarExpanded);
    if (sidebarExpanded) {
      document.querySelector("body").classList.add("sidebar-expanded");
    } else {
      document.querySelector("body").classList.remove("sidebar-expanded");
    }
  }, [sidebarExpanded]);

  return (
    <div className="min-w-fit">
      {/* Sidebar backdrop (mobile only) */}
      <div
        className={`fixed inset-0 bg-gray-900 bg-opacity-50 z-40 lg:hidden lg:z-auto transition-opacity duration-200 ${
          sidebarOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        aria-hidden="true"
      ></div>

      {/* Sidebar */}
      <div
        id="sidebar"
        ref={sidebar}
        className={`flex lg:!flex flex-col absolute z-40 left-0 top-0 lg:static lg:left-auto lg:top-auto lg:translate-x-0 h-[100dvh] overflow-y-auto w-64 lg:w-20 lg:sidebar-expanded:!w-64 2xl:!w-64 shrink-0 p-4 transition-all duration-200 ease-in-out studio-scrollbar ${sidebarOpen ? "translate-x-0" : "-translate-x-64"}`}
        style={{
          background: 'rgba(52, 73, 94, 0.95)',
          backdropFilter: 'blur(10px)',
          borderRight: '2px solid rgba(241, 196, 15, 0.3)'
        }}
      >
        {/* Sidebar header */}
        <div className="flex justify-between items-center mb-8 pr-3 sm:px-2">
          {/* Close button */}
          <button
            ref={trigger}
            className="lg:hidden text-yellow-400 hover:text-yellow-300 transition-colors"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-controls="sidebar"
            aria-expanded={sidebarOpen}
          >
            <span className="sr-only">Close sidebar</span>
            <svg className="w-6 h-6 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M10.7 18.7l1.4-1.4L7.8 13H20v-2H7.8l4.3-4.3-1.4-1.4L4 12z" />
            </svg>
          </button>
          
          {/* Logo */}
          <NavLink end to="/studio/dashboard" className="block">
            {/* Collapsed Logo - shows when sidebar is collapsed */}
            <div className="lg:block lg:sidebar-expanded:hidden 2xl:hidden text-center">
              <div className="text-lg font-black" style={{
                color: '#F1C40F',
                textShadow: '1px 1px 0px #E67E22'
              }}>
                GCH
              </div>
              <div className="text-xs px-1 py-0.5 border rounded font-bold" style={{
                backgroundColor: '#E74C3C',
                borderColor: '#F1C40F',
                color: '#FFFFFF',
                textShadow: '1px 1px 0px #C0392B'
              }}>
                S
              </div>
            </div>
            
            {/* Expanded Logo - shows when sidebar is expanded */}
            <div className="lg:sidebar-expanded:block 2xl:block hidden">
              <div className="text-xl font-black tracking-wider whitespace-nowrap" style={{
                color: '#F1C40F',
                textShadow: '1px 1px 0px #E67E22, 2px 2px 0px #D35400'
              }}>
                GROUP CHAT HEROES
              </div>
              <div className="text-base px-2 py-1 border border-yellow-400 rounded font-bold text-center mt-1" style={{
                backgroundColor: '#E74C3C',
                borderColor: '#F1C40F',
                color: '#FFFFFF',
                textShadow: '1px 1px 0px #C0392B'
              }}>
                STUDIO
              </div>
            </div>
          </NavLink>
        </div>

        {/* Agency client selector */}
        {subscriptionType === 'agency' && (
          <div className="mb-6">
            <ClientSelector sidebarExpanded={sidebarExpanded} />
          </div>
        )}

        {/* Welcome Section */}
        <div className="mb-6 px-3">
          <div className="text-xs uppercase font-bold mb-2" style={{color: '#F1C40F'}}>
            <span className="hidden lg:block lg:sidebar-expanded:hidden 2xl:hidden text-center">
              ⚡
            </span>
            <span className="lg:hidden lg:sidebar-expanded:block 2xl:block">
              {userName ? `WELCOME, ${userName.toUpperCase()}` : 'WELCOME, HERO'}
            </span>
          </div>
          <div className="lg:hidden lg:sidebar-expanded:block 2xl:block">
            {/* Removed "Ready to create epic games? 🚀" text */}
          </div>
        </div>

        {/* Navigation Links */}
        <div className="space-y-2 flex-1">
          {/* Dashboard */}
          <NavLink
            end
            to="/studio/dashboard"
            className={({ isActive }) =>
              `flex items-center px-3 py-3 rounded-lg font-bold transition-all duration-200 ${
                isActive
                  ? 'text-white shadow-lg'
                  : 'text-gray-300 hover:text-white hover:bg-yellow-400/10'
              }`
            }
            style={({ isActive }) => isActive ? {
              background: 'linear-gradient(135deg, #F1C40F, #F39C12)',
              boxShadow: '0 4px 12px rgba(241, 196, 15, 0.3)',
              textShadow: '1px 1px 0px #E67E22'
            } : {}}
          >
            <span className="text-lg shrink-0">📊</span>
            <span className="ml-3 lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
              Dashboard
            </span>
          </NavLink>

          {/* Campaigns */}
          <NavLink
            end
            to="/studio/campaigns"
            className={({ isActive }) =>
              `flex items-center px-3 py-3 rounded-lg font-bold transition-all duration-200 ${
                isActive
                  ? 'text-white shadow-lg'
                  : 'text-gray-300 hover:text-white hover:bg-yellow-400/10'
              }`
            }
            style={({ isActive }) => isActive ? {
              background: 'linear-gradient(135deg, #F1C40F, #F39C12)',
              boxShadow: '0 4px 12px rgba(241, 196, 15, 0.3)',
              textShadow: '1px 1px 0px #E67E22'
            } : {}}
          >
            <span className="text-lg shrink-0">🚀</span>
            <span className="ml-3 lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
              Campaigns
            </span>
          </NavLink>

          {/* Game Library */}
          <NavLink
            end
            to="/studio/games"
            className={({ isActive }) =>
              `flex items-center px-3 py-3 rounded-lg font-bold transition-all duration-200 ${
                isActive
                  ? 'text-white shadow-lg'
                  : 'text-gray-300 hover:text-white hover:bg-yellow-400/10'
              }`
            }
            style={({ isActive }) => isActive ? {
              background: 'linear-gradient(135deg, #F1C40F, #F39C12)',
              boxShadow: '0 4px 12px rgba(241, 196, 15, 0.3)',
              textShadow: '1px 1px 0px #E67E22'
            } : {}}
          >
            <span className="text-lg shrink-0">🎯</span>
            <span className="ml-3 lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
              Game Library
            </span>
          </NavLink>

          {/* Clients - only visible to agency users when not impersonating */}
          {subscriptionType === 'agency' && businessProfile?.id === userProfile?.companyId && (
            <NavLink
              end
              to="/studio/clients"
              className={({ isActive }) =>
                `flex items-center px-3 py-3 rounded-lg font-bold transition-all duration-200 ${
                  isActive
                    ? 'text-white shadow-lg'
                    : 'text-gray-300 hover:text-white hover:bg-yellow-400/10'
                }`
              }
              style={({ isActive }) => isActive ? {
                background: 'linear-gradient(135deg, #F1C40F, #F39C12)',
                boxShadow: '0 4px 12px rgba(241, 196, 15, 0.3)',
                textShadow: '1px 1px 0px #E67E22'
              } : {}}
            >
              <span className="text-lg shrink-0">👥</span>
              <span className="ml-3 lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                Clients
              </span>
            </NavLink>
          )}

          {/* Settings */}
          <SidebarLinkGroup activecondition={pathname.includes("settings")}>
            {(handleClick, open) => {
              const isActive = pathname.includes("settings");
              return (
                <React.Fragment>
                  <button
                    className={`w-full flex items-center justify-between px-3 py-3 rounded-lg font-bold transition-all duration-200 ${
                      isActive
                        ? 'text-white shadow-lg'
                        : 'text-gray-300 hover:text-white hover:bg-yellow-400/10'
                    }`}
                    style={isActive ? {
                      background: 'linear-gradient(135deg, #F1C40F, #F39C12)',
                      boxShadow: '0 4px 12px rgba(241, 196, 15, 0.3)',
                      textShadow: '1px 1px 0px #E67E22'
                    } : {}}
                    onClick={(e) => {
                      e.preventDefault();
                      handleClick();
                      setSidebarExpanded(true);
                    }}
                  >
                    <div className="flex items-center">
                      <span className="text-lg shrink-0">⚙️</span>
                      <span className="ml-3 lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                        Settings
                      </span>
                    </div>
                    <svg 
                      className={`w-3 h-3 shrink-0 transition-transform duration-200 lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 ${open ? "rotate-180" : ""}`} 
                      viewBox="0 0 12 12"
                      fill="currentColor"
                    >
                      <path d="M5.9 11.4L.5 6l1.4-1.4 4 4 4-4L11.3 6z" />
                    </svg>
                  </button>
                  
                  <div className="lg:hidden lg:sidebar-expanded:block 2xl:block">
                    <ul className={`mt-2 space-y-1 ${!open && "hidden"}`}>
                      <li>
                        <NavLink
                          end
                          to="/studio/settings/account"
                          className={({ isActive }) =>
                            `block px-6 py-2 text-sm font-bold rounded transition-colors duration-200 ${
                              isActive 
                                ? 'text-yellow-400 bg-yellow-400/10' 
                                : 'text-gray-400 hover:text-yellow-300'
                            }`
                          }
                        >
                          Account
                        </NavLink>
                      </li>
                      <li>
                        <NavLink
                          end
                          to="/studio/settings/company"
                          className={({ isActive }) =>
                            `block px-6 py-2 text-sm font-bold rounded transition-colors duration-200 ${
                              isActive 
                                ? 'text-yellow-400 bg-yellow-400/10' 
                                : 'text-gray-400 hover:text-yellow-300'
                            }`
                          }
                        >
                          Company
                        </NavLink>
                      </li>
                      <li>
                        <NavLink
                          end
                          to="/studio/settings/plans"
                          className={({ isActive }) =>
                            `block px-6 py-2 text-sm font-bold rounded transition-colors duration-200 ${
                              isActive 
                                ? 'text-yellow-400 bg-yellow-400/10' 
                                : 'text-gray-400 hover:text-yellow-300'
                            }`
                          }
                        >
                          Plans
                        </NavLink>
                      </li>
                      {subscriptionType === 'agency' && businessProfile?.id === userProfile?.companyId && (
                        <li>
                          <NavLink
                            end
                            to="/studio/settings/users"
                            className={({ isActive }) =>
                              `block px-6 py-2 text-sm font-bold rounded transition-colors duration-200 ${
                                isActive 
                                  ? 'text-yellow-400 bg-yellow-400/10' 
                                  : 'text-gray-400 hover:text-yellow-300'
                              }`
                            }
                          >
                            Users
                          </NavLink>
                        </li>
                      )}
                    </ul>
                  </div>
                </React.Fragment>
              );
            }}
          </SidebarLinkGroup>

          {/* Admin - only visible to rob@groupchatheroes.com */}
          {isAdmin && (
            <NavLink
              end
              to="/studio/admin"
              className={({ isActive }) =>
                `flex items-center px-3 py-3 rounded-lg font-bold transition-all duration-200 ${
                  isActive
                    ? 'text-white shadow-lg'
                    : 'text-gray-300 hover:text-white hover:bg-yellow-400/10'
                }`
              }
              style={({ isActive }) => isActive ? {
                background: 'linear-gradient(135deg, #F1C40F, #F39C12)',
                boxShadow: '0 4px 12px rgba(241, 196, 15, 0.3)',
                textShadow: '1px 1px 0px #E67E22'
              } : {}}
            >
              <span className="text-lg shrink-0">🔧</span>
              <span className="ml-3 lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                Admin
              </span>
            </NavLink>
          )}
        </div>

        {/* Bottom Section - Live Campaign Status */}
        <LiveCampaignStatus />

        {/* Expand / collapse button */}
        <div className="pt-3 hidden lg:inline-flex 2xl:hidden justify-center mt-4">
          <button 
            className="text-yellow-400 hover:text-yellow-300 transition-colors p-2 rounded-lg hover:bg-yellow-400/10" 
            onClick={() => setSidebarExpanded(!sidebarExpanded)}
          >
            <span className="sr-only">Expand / collapse sidebar</span>
            <svg 
              className={`shrink-0 w-4 h-4 fill-current transition-transform duration-200 ${sidebarExpanded ? 'rotate-180' : ''}`} 
              xmlns="http://www.w3.org/2000/svg" 
              viewBox="0 0 16 16"
            >
              <path d="M15 16a1 1 0 0 1-1-1V1a1 1 0 1 1 2 0v14a1 1 0 0 1-1 1ZM8.586 7H1a1 1 0 1 0 0 2h7.586l-2.793 2.793a1 1 0 1 0 1.414 1.414l4.5-4.5A.997.997 0 0 0 12 8.01M11.924 7.617a.997.997 0 0 0-.217-.324l-4.5-4.5a1 1 0 0 0-1.414 1.414L8.586 7M12 7.99a.996.996 0 0 0-.076-.373Z" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

export default Sidebar;
